import { BILLING_METHODS, PageName } from 'lib/constants';
import { Organization, ScormMode } from 'lib/types/company';
import { IPageProps } from 'lib/types/page';
import { ISession } from 'lib/types/session';
import _ from 'lodash';
import { FeatureName, PermissionName } from './types';

/**
 * This is the singular source of user page access.
 */
export const getRequiredPermissionForPage = (
  pageName: PageName
): PermissionName => {
  switch (pageName) {
    case PageName.ADMIN_ANALYTICS:
      // TODO (Course Permissions): Possibly add a VIEW_ANALYTICS permission?
      return PermissionName.MANAGE_COURSE_PERMISSIONS;

    case PageName.ADMIN_CASE_MANAGEMENT:
      return PermissionName.CASE_MANAGEMENT;

    case PageName.ADMIN_CASE_MANAGEMENT_UPSELL:
      return PermissionName.ADMIN_VIEW;

    case PageName.ADMIN_ASSIGNMENT:
      return PermissionName.ADMINISTER_TRAINING;

    case PageName.ADMIN_CONTENT_LIBRARY:
      return PermissionName.ADMINISTER_TRAINING;

    case PageName.ADMIN_DASHBOARD:
      return PermissionName.ADMINISTER_TRAINING;

    case PageName.ADMIN_ETHENA_INVOICE_CONTENT_LIBRARY:
      return PermissionName.ADMINISTER_TRAINING;

    case PageName.ADMIN_EVENTS:
      return PermissionName.ADMINISTER_TRAINING;

    case PageName.ADMIN_TRAINING_PREVIEW:
      return PermissionName.ADMINISTER_TRAINING;

    case PageName.ADMIN_SETTINGS:
      return PermissionName.VIEW_COMPANY_SETTINGS;

    case PageName.ADMIN_TEAM_INSIGHTS:
      return PermissionName.ADMINISTER_TRAINING;

    case PageName.ADMIN_TEAM_MEMBERS:
      return PermissionName.MANAGE_TEAM_LIST;

    case PageName.INTERNAL:
      return PermissionName.INTERNAL_VIEW;

    case PageName.INTERNAL_COURSES:
      return PermissionName.INTERNAL_COURSES;

    case PageName.INTERNAL_SEARCH:
      return PermissionName.INTERNAL_SEARCH;

    case PageName.INTERNAL_QUESTIONS:
      return PermissionName.INTERNAL_QUESTIONS;

    case PageName.INTERNAL_COMPANIES:
      return PermissionName.INTERNAL_COMPANIES;

    case PageName.INTERNAL_TEMPLATES:
      return PermissionName.INTERNAL_TEMPLATES;

    case PageName.INTERNAL_PREVIEW_LINKS:
      return PermissionName.INTERNAL_PREVIEW_LINKS;

    case PageName.INTERNAL_PARTNERS:
      return PermissionName.INTERNAL_PARTNERS;

    case PageName.INTERNAL_THEMES:
      return PermissionName.INTERNAL_THEMES;

    case PageName.INTERNAL_USER_ROLES:
      return PermissionName.INTERNAL_USER_ROLES;

    case PageName.INTERNAL_STRING_VARIABLES:
      return PermissionName.INTERNAL_STRING_VARIABLES;

    case PageName.INTERNAL_VIDEO_LIBRARY:
      return PermissionName.INTERNAL_COURSES;

    default:
      return PermissionName.ADMIN_VIEW;
  }
};

/**
 * This is the singular source of company page access.
 */
const getIsPageAccessibleToCompany = (
  pageName: PageName,
  session: ISession
): boolean => {
  if (!session) {
    return false;
  }

  switch (pageName) {
    case PageName.ADMIN_ANALYTICS:
      return getIsFeatureEnabledForCompany(FeatureName.ANALYTICS, session);

    case PageName.ADMIN_CASE_MANAGEMENT:
      return (
        getIsFeatureEnabledForCompany(FeatureName.CASE_MANAGEMENT, session) ||
        getIsFeatureEnabledForCompany(
          FeatureName.HOTLINE_AND_CASE_MANAGEMENT,
          session
        )
      );

    case PageName.ADMIN_CASE_MANAGEMENT_UPSELL:
      return (
        !(
          getIsFeatureEnabledForCompany(FeatureName.CASE_MANAGEMENT, session) ||
          getIsFeatureEnabledForCompany(
            FeatureName.HOTLINE_AND_CASE_MANAGEMENT,
            session
          )
        ) && session.company.organization === Organization.ETHENA
      );

    case PageName.ADMIN_PHISHING:
      return getIsFeatureEnabledForCompany(FeatureName.PHISHING, session);

    case PageName.ADMIN_DASHBOARD:
    case PageName.ADMIN_TEAM_MEMBERS:
    case PageName.TRAINING_CENTER:
      // This is to restrict it being shown to SCORM_ONLY companies in the nav.
      // Not a long term solution.
      // https://github.com/goethena/ethena-fe/pull/2143#discussion_r1414480686
      return !getIsFeatureEnabledForCompany(FeatureName.SCORM_ONLY, session);

    case PageName.ADMIN_TEAM_INSIGHTS:
      return (
        !getIsFeatureEnabledForCompany(FeatureName.SCORM_ONLY, session) &&
        getIsFeatureEnabledForCompany(FeatureName.TEAM_INSIGHTS, session)
      );

    case PageName.ADMIN_ETHENA_INVOICE_CONTENT_LIBRARY:
      return (
        session.company.organization === Organization.ETHENA &&
        session.company.billingMethod === BILLING_METHODS.INVOICE &&
        getIsFeatureEnabledForCompany(FeatureName.LMS_ESSENTIALS, session)
      );

    case PageName.ADMIN_EVENTS:
      return getIsFeatureEnabledForCompany(FeatureName.EVENTS, session);

    default:
      return true;
  }
};

export const getIsFeatureEnabledForCompany = (
  featureName: FeatureName,
  session: ISession
): boolean => {
  if (!session) {
    return false;
  }

  switch (featureName) {
    case FeatureName.ANALYTICS:
      return session.configurations.isAnalyticsEnabled;

    case FeatureName.CASE_MANAGEMENT:
      return session.configurations.isMisconductReportingCaseManagementEnabled;

    case FeatureName.HOTLINE_AND_CASE_MANAGEMENT:
      return session.configurations.isMisconductReportingEnabled;

    case FeatureName.LMS_ESSENTIALS:
      return session.configurations.isCustomCourseImportEnabled;

    case FeatureName.PHISHING:
      return session.configurations.isPhishingCampaignsEnabled;

    case FeatureName.SCORM:
      return session.configurations.scormMode !== ScormMode.DISABLED;

    case FeatureName.SCORM_ONLY:
      return session.configurations.scormMode === ScormMode.SCORM_ONLY;

    case FeatureName.TEAM_INSIGHTS:
    case FeatureName.COMPANY_POLICIES:
      return session.company.organization !== Organization.LIFE_LABS;

    case FeatureName.EVENTS:
      return session.company.organization === Organization.LIFE_LABS;

    case FeatureName.USERS_WITHOUT_EMAILS:
      return session.configurations.allowUsersWithoutEmails;

    default:
      return false;
  }
};

export const getIsUserCompanyAdmin = (session: ISession): boolean =>
  getIsUserInPermission(PermissionName.ADMIN_VIEW, session);

export const getIsUserInternalAdmin = (session: ISession): boolean =>
  getIsUserInPermission(PermissionName.INTERNAL_VIEW, session);

export const getIsUserInPermission = (
  requiredPermission: PermissionName,
  session: ISession
): boolean => {
  if (!session) {
    return false;
  }
  return (
    _.includes(session.permissions, PermissionName.INTERNAL_SUPER) ||
    _.includes(session.permissions, requiredPermission)
  );
};

export const getIsPageAccessibleToUser = (
  pageName: PageName,
  session: ISession
): boolean => {
  if (!session) {
    return false;
  }

  const isPageAccessibleToCompany = getIsPageAccessibleToCompany(
    pageName,
    session
  );

  const requiredPermission = getRequiredPermissionForPage(pageName);
  const isUserInPermission = getIsUserInPermission(requiredPermission, session);

  return isPageAccessibleToCompany && isUserInPermission;
};

export const withAuthorization = <T extends IPageProps>(
  pageName: PageName,
  pageProps: T
): T => {
  if (!pageProps) {
    return { ...pageProps, error: true, message: 'Unauthorized' };
  }

  const hasAccessToPage = getIsPageAccessibleToUser(
    pageName,
    pageProps.session
  );

  // We need to pass down page props here so we can brand the error page
  if (!hasAccessToPage) {
    return { ...pageProps, error: true, message: 'Unauthorized' };
  }

  return pageProps;
};
